import { useCallback, useEffect, useState } from "react";
import type { ChatSession } from "@stockline/shared/types/ai/chat-session";

const keyFor = (surface: string) => `chat:selected:${surface}`;

// Blocked storage (private mode, a locked-down webview) throws on access, and
// `readSelection` runs during render — unguarded, it would take the whole chat
// surface down over an optional convenience. Degrade to "no memory", loudly.
function storageUnavailable(err: unknown) {
  console.warn(
    "[chat] no session storage — conversations won't be remembered",
    err,
  );
}

export function readSelection(surface: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(keyFor(surface));
  } catch (err) {
    storageUnavailable(err);
    return null;
  }
}

export function writeSelection(surface: string, id: string | null) {
  if (typeof window === "undefined") return;
  try {
    if (id) window.sessionStorage.setItem(keyFor(surface), id);
    else window.sessionStorage.removeItem(keyFor(surface));
  } catch (err) {
    storageUnavailable(err);
  }
}

/**
 * Reopens the conversation this tab had open, and returns a `remember` to record
 * the next one.
 *
 * `surface` namespaces the memory — chat surfaces can be mounted at once, and a
 * shared key would let each clobber the other's thread. `onRestore` must be
 * stable. Pass `enabled: false` when something else already owns the opening
 * conversation (a handed-off prompt), so the memory does not fight it.
 */
export function useSelectionMemory(
  surface: string,
  sessions: ChatSession[] | undefined,
  onRestore: (id: string) => void,
  enabled = true,
) {
  const [pendingId, setPendingId] = useState(() =>
    enabled ? readSelection(surface) : null,
  );

  useEffect(() => {
    if (!pendingId) return;
    const session = sessions?.find((s) => s.id === pendingId);
    // Absent: still hydrating, or gone. Presence is the only available proof — a
    // single-row lookup reads null in both cases — so absence is never taken as
    // deletion, and a late-arriving row re-runs this.
    if (!session) return;
    setPendingId(null);
    // Unread again: a reply landed while away, and reopening it here would clear
    // the inbox badge for something the user never read.
    if (!session.unread) onRestore(pendingId);
  }, [sessions, pendingId, onRestore]);

  return useCallback(
    (id: string | null) => {
      setPendingId(null);
      writeSelection(surface, id);
    },
    [surface],
  );
}
